export const hexToHSL = (hex) => {
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  }
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
};

export const hslToHex = (h, s, l) => {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

export const getDegradedColor = (hex, lighten = true) => {
  const { h, s, l } = hexToHSL(hex);
  const newLightness = lighten ? Math.min(90, l + 15) : Math.max(10, l - 15);
  return hslToHex(h, s, newLightness);
};

export const normalizeTheme = (theme) => {
  if (!theme) return '';
  return theme;
};

export const normalizeThemeForComparison = (theme) => {
  return normalizeTheme(theme).toLowerCase();
};

export const getFlashcardScore = (box) => {
  switch (parseInt(box)) {
    case 1: return 0;
    case 2: return 40;
    case 3: return 70;
    case 4: return 90;
    case 5: return 100;
    default: return 0;
  }
};

export const calculateStudiedCountToday = (theme, learnData) => {
  const normalizedTheme = normalizeThemeForComparison(theme);
  const today = new Date().toISOString().split('T')[0];
  return Object.entries(learnData).reduce((count, [id, data]) => {
    const themes = (data.themes || []).map(normalizeThemeForComparison);
    if (themes.includes(normalizedTheme) && data.doneToday === today) {
      return count + 1;
    }
    return count;
  }, 0);
};

export const calculateTotalStudiedCount = (theme, learnData) => {
  const normalizedTheme = normalizeThemeForComparison(theme);
  return Object.entries(learnData).reduce((count, [id, data]) => {
    const themes = (data.themes || []).map(normalizeThemeForComparison);
    if (themes.includes(normalizedTheme) && data.doneToday !== null) {
      return count + 1;
    }
    return count;
  }, 0);
};

export const calculateSessionCountToday = (theme, statistics) => {
  const normalizedTheme = normalizeThemeForComparison(theme);
  const today = new Date().toISOString().split('T')[0];
  const daily = statistics?.dailySessions?.[today] || {}; // Vérification avec optional chaining
  return Object.keys(daily).reduce((sum, key) => {
    if (normalizeThemeForComparison(key) === normalizedTheme) return sum + (daily[key] || 0);
    return sum;
  }, 0);
};

export const calculateTotalSessionCount = (theme, statistics) => {
  const normalizedTheme = normalizeThemeForComparison(theme);
  const total = statistics.totalSessionsByTheme || {};
  return Object.keys(total).reduce((sum, key) => {
    if (normalizeThemeForComparison(key) === normalizedTheme) return sum + (total[key] || 0);
    return sum;
  }, 0);
};

export const calculateThemeScore = (theme, learnData, learnScore) => {
  const normalizedTheme = normalizeTheme(theme);
  const normalizedLower = normalizeThemeForComparison(theme);
  return learnScore[normalizedTheme] || learnScore[normalizedLower] || 0;
};

export const selectWeightedItems = (itemIds, learnData = {}) => {
  // Attribution des poids spécifiques par box
  const getWeight = (box) => {
    switch (parseInt(box)) {
      case 1: return 1;
      case 2: return 1 / 3;
      case 3: return 1 / 7;
      case 4: return 1 / 14;
      case 5: return 1 / 30;
      default: return 1; // Box par défaut à 1 si non défini
    }
  };

  // Utilisation de let au lieu de const pour permettre la modification
  let weightedItems = itemIds.map((id) => ({
    id,
    weight: getWeight(learnData[id]?.box || '1'),
  }));

  const totalWeight = weightedItems.reduce((sum, item) => sum + item.weight, 0);
  const selectedItems = [];
  const seenIds = new Set();

  while (selectedItems.length < 20 && weightedItems.length > 0) {
    const rand = Math.random() * totalWeight;
    let cumulativeWeight = 0;

    for (let i = 0; i < weightedItems.length; i++) {
      cumulativeWeight += weightedItems[i].weight;
      if (rand <= cumulativeWeight) {
        const selected = weightedItems.splice(i, 1)[0];
        if (!seenIds.has(selected.id)) {
          selectedItems.push(selected.id);
          seenIds.add(selected.id);
        }
        break;
      }
    }
  }

  if (selectedItems.length === 0) {
    console.warn('Aucune carte sélectionnée après pondération');
    return itemIds.slice(0, 20); // Fallback : prendre les 20 premières cartes
  }

  return selectedItems;
};